import React, { ReactElement } from 'react';
import { Field, Form } from 'react-final-form';
import { connect } from 'react-redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import queryString from 'query-string';
import { RouteComponentProps } from 'react-router-dom';
import { AxiosPromise } from 'axios';
import { AnyAction } from 'redux';

import { validatorRequired, validatorMinValue6 } from '~src/services/validators';
import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { orderedColorsSets } from '~src/styles/colors';
import {
  PasswordBoxContainer,
  PasswordForm,
  PasswordHeader,
  PasswordSubline,
  PasswordTitleHeader,
} from '~src/outer_modules/shared/shared.sc';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { UnauthorizedPagesContainer } from '~src/outer_modules/shared/shared.sc';
import TosAndDataPrivacyInfoBox from '~src/outer_modules/shared/tosAndDataPrivacyInfoBox/TosAndDataPrivacyInfoBox';
import IconInput, { InputIconType } from '~src/shared/form/inputs/IconInput';
import {
  setRedirect,
  logout,
  initUserLogin,
  redeemSessionToken,
  setPassword,
} from '~src/store/session/actions';
import { StoreI } from '~src/rootReducer';
import { RedeemTokenRespI } from '~src/store/session/interfaces';
import { FieldType, EsModule } from '~src/types';
import { setup } from '../../../config/server/server.config';

import {
  SecurityBlock,
  SecurityBlocksContainer,
  SubmitBtn,
  ToHomeBtn,
  TokenErrorContainer,
} from './newPassword.sc';

let zxcvbn: EsModule<any>;

interface UrlSearchI {
  location?: string;
  secureAccount?: string;
  hideTos?: string;
  auth_token?: string;
}

interface FormValuesI {
  password: string;
  passwordConfirm: string;
}

interface StateProps {
  redirect: string | null;
}

interface DispatchProps {
  setPassword(password: FormValuesI): AxiosPromise;

  logout(): void;

  redeemToken(token?: string): Promise<RedeemTokenRespI>;

  setRedirect(redirect: string): AnyAction;

  initUserLogin(token: string): Promise<[]>;
}

type Props = StateProps & DispatchProps & WithTranslation & RouteComponentProps<any>;

interface State {
  authToken?: string;
  loginToken: string;
  invalidToken: boolean;
  loadingToken: boolean;
  showSecureAccountBeforeProceed: boolean;
  hideTos: boolean;
  loadingModule: boolean;
}

class NewPassword extends React.Component<Props, State> {
  static defaultProps = {
    redirect: null,
  };

  isUnmounted = false;

  constructor(props: Props) {
    super(props);
    this.state = {
      authToken: '',
      loginToken: '',
      invalidToken: false,
      loadingToken: false,
      showSecureAccountBeforeProceed: false,
      hideTos: false,
      loadingModule: true,
    };
    this.loadZxcvb();
  }

  componentDidMount() {
    this.props.logout();

    const {
      location: redirectPath,
      secureAccount,
      hideTos,
      auth_token,
    } = queryString.parse(this.props.location.search) as UrlSearchI;
    if (redirectPath) {
      this.props.setRedirect(redirectPath);
    }

    if (secureAccount) {
      this.setState({ showSecureAccountBeforeProceed: true });
    }

    if (hideTos) {
      this.setState({ hideTos: true });
    }

    this.setState({ authToken: auth_token }, () => {
      this.redeemToken(this.state.authToken);
    });
  }

  componentWillUnmount() {
    localStorage.removeItem('jwt_TMP');
    this.isUnmounted = true;
  }

  onSubmit = (value: FormValuesI) => {
    const jwtTmp = localStorage.getItem('jwt_TMP');
    if (jwtTmp) {
      localStorage.setItem('jwt', jwtTmp);
      localStorage.removeItem('jwt_TMP');
    }

    return this.props.setPassword(value).then(async () => {
      if (!this.isUnmounted) {
        localStorage.removeItem('statToken');
        localStorage.removeItem('actToken');
        await this.props.initUserLogin(this.state.loginToken);
        const { location: fixedLocation } = queryString.parse(this.props.location.search) as {
          location?: string;
        };
        if (fixedLocation === 'spaceloop') {
          document.location.replace(setup.SPACELOOP_FE);
          return;
        }
        let location = '/';
        if (this.props.redirect) {
          location = `login?location=${this.props.redirect}`;
        }
        this.props.history.push(location);
      }
    });
  };

  async loadZxcvb() {
    zxcvbn = await import(/* webpackChunkName: "zxcvbn" */ 'zxcvbn');
    this.setState({ loadingModule: false });
  }

  redeemToken(token?: string): Promise<RedeemTokenRespI | void> {
    this.setState({ loadingToken: true });
    return this.props
      .redeemToken(token)
      .then(response => {
        if (!this.isUnmounted) {
          this.setState({ loginToken: response.token });
          localStorage.setItem('jwt_TMP', response.token);
        }
      })
      .catch(() => {
        if (!this.isUnmounted) {
          this.setState({ invalidToken: true });
        }
      })
      .finally(() => {
        if (!this.isUnmounted) {
          this.setState({ loadingToken: false });
        }
      });
  }

  populateSecBlock(score?: number): ReactElement[] | null {
    if (typeof score !== 'number') {
      return null;
    }
    const secBlock: ReactElement[] = [];
    for (let i = 0; i <= score; i += 1) {
      secBlock.push(<SecurityBlock key={i} color={orderedColorsSets[5][score]} />);
    }
    return secBlock;
  }

  renderField = ({
    autoFocus,
    input,
    label,
    meta: { touched, error },
    labelKey,
  }: FieldType & { type: InputIconType }) => (
    <IconInput
      {...input}
      autoFocus={autoFocus}
      label={label}
      type={input.type as InputIconType}
      error={touched ? error : undefined}
      labelKey={labelKey}
    />
  );

  goHome = () => this.props.history.push('/');

  formValidator = (values: FormValuesI): Partial<Record<keyof FormValuesI, string>> => {
    const errors: Partial<Record<keyof FormValuesI, string>> = {};
    if (values && values.password && values.passwordConfirm) {
      if (values.password !== values.passwordConfirm) {
        errors.passwordConfirm = 'passwords_mismatch';
      }
    }
    return errors;
  };

  render() {
    const { t } = this.props;

    return (
      <UnauthorizedPagesContainer>
        {this.state.loadingToken || this.state.loadingModule ? (
          <LoadingSpinner color="white" alignCls="center-middle" />
        ) : (
          <PasswordBoxContainer>
            <Form onSubmit={this.onSubmit} validate={this.formValidator}>
              {({ handleSubmit, submitting, values }) =>
                this.state.invalidToken ? (
                  <TokenErrorContainer data-translation="x.set_password.token_expired.title">
                    {t('x.set_password.token_expired.title')}
                    <ToHomeBtn onClick={this.goHome} $buttonColor="white">
                      <span data-translation="x.set_password.token_expired.button">
                        {t('x.set_password.token_expired.button')}
                      </span>
                    </ToHomeBtn>
                  </TokenErrorContainer>
                ) : (
                  <>
                    <PasswordHeader>
                      <PasswordTitleHeader data-translation="x.set_password.header.text | x.set_password.secure_account_override.header.text">
                        {this.state.showSecureAccountBeforeProceed
                          ? t('x.set_password.secure_account_override.header.text')
                          : t('x.set_password.header.text')}
                      </PasswordTitleHeader>
                      <PasswordSubline data-translation="x.set_password.header.subline.text | x.set_password.secure_account_override.header.subline.text">
                        {this.state.showSecureAccountBeforeProceed
                          ? t('x.set_password.secure_account_override.header.subline.text')
                          : t('x.set_password.header.subline.text')}
                      </PasswordSubline>
                    </PasswordHeader>
                    <PasswordForm onSubmit={handleSubmit} noValidate>
                      <Field
                        autoFocus
                        name="password"
                        label={t('x.set_password.input.placeholder.password')}
                        type="password"
                        children={this.renderField}
                        validate={composeValidators([validatorRequired, validatorMinValue6])}
                        labelKey="x.set_password.input.placeholder.password"
                      />
                      <SecurityBlocksContainer>
                        {values.password && this.populateSecBlock(zxcvbn.default(values.password))}
                      </SecurityBlocksContainer>
                      <Field
                        name="passwordConfirm"
                        label={t('x.set_password.input.placeholder.repeat_password')}
                        type="password"
                        children={this.renderField}
                        validate={composeValidators([validatorRequired, validatorMinValue6])}
                        labelKey="x.set_password.input.placeholder.repeat_password"
                      />
                      <SecurityBlocksContainer>
                        {values.passwordConfirm &&
                          this.populateSecBlock(zxcvbn.default(values.passwordConfirm))}
                      </SecurityBlocksContainer>
                      <SubmitBtn isLoading={submitting} $buttonColor="reddish">
                        <span data-translation="x.set_password.button.text">
                          {t('x.set_password.button.text')}
                        </span>
                      </SubmitBtn>
                    </PasswordForm>
                    {!this.state.hideTos && <TosAndDataPrivacyInfoBox t={t} />}
                  </>
                )
              }
            </Form>
          </PasswordBoxContainer>
        )}
      </UnauthorizedPagesContainer>
    );
  }
}

const mapState2Props = (state: StoreI): StateProps => ({
  redirect: state.user.session.redirect,
});

const mapDispatch2Props = (dispatch: any): DispatchProps => ({
  setPassword: password => dispatch(setPassword(password)),
  logout: () => dispatch(logout),
  redeemToken: token => dispatch(redeemSessionToken(token)),
  setRedirect: redirect => dispatch(setRedirect(redirect)),
  initUserLogin: token => dispatch(initUserLogin(token)),
});

const NewPasswordT = withTranslation()(NewPassword);
const NewPasswordC = connect(mapState2Props, mapDispatch2Props)(NewPasswordT);

export default NewPasswordC;
